import { useCallback, useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { authApi } from '@/api/endpoints/auth';
import { groupApi } from '@/api/endpoints/group';
import { getCompanySwitchInProgress } from '@/state/companySwitchState';

// Don't re-hit /auth/me more than once per this window on rapid focus changes.
const REVALIDATE_THROTTLE_MS = 10_000;

export function useSessionValidator() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const logout = useAuthStore((s) => s.logout);
  const setUser = useAuthStore((s) => s.setUser);
  const setBootstrapped = useAuthStore((s) => s.setBootstrapped);
  const updateCompanyModules = useAuthStore((s) => s.updateCompanyModules);
  const updateCompanyPermissions = useAuthStore((s) => s.updateCompanyPermissions);
  const updateCompanyTheme = useAuthStore((s) => s.updateCompanyTheme);
  const updateCompanyFeatureFlags = useAuthStore((s) => s.updateCompanyFeatureFlags);
  const setPlatformRole = useAuthStore((s) => s.setPlatformRole);
  const setImpersonation = useAuthStore((s) => s.setImpersonation);
  const setGroupPaletteKey = useUIStore((s) => s.setGroupPaletteKey);

  const lastRevalidateRef = useRef(0);

  // Re-fetch /auth/me and live-apply per-company config (modules, permissions,
  // theme, feature flags) plus platform role / impersonation. This is what makes
  // a Console-side change (theme, feature flag, module entitlement) reach the
  // product without a full re-login. updateCompany* actions no-op when unchanged,
  // so re-running this is cheap and won't churn the store.
  const revalidateSession = useCallback(
    async (opts?: { logoutOnError?: boolean }) => {
      lastRevalidateRef.current = Date.now();
      try {
        const res = await authApi.getMe();
        // A company switch sets authoritative state from its own response; don't
        // clobber it with a possibly-older /auth/me payload mid-switch.
        if (getCompanySwitchInProgress()) return;
        if (res.data?.companies) {
          for (const c of res.data.companies) {
            updateCompanyModules(c.company_id, c.enabled_modules ?? []);
            updateCompanyPermissions(c.company_id, c.permissions ?? []);
            updateCompanyTheme(c.company_id, c.theme);
            updateCompanyFeatureFlags(c.company_id, c.feature_flags ?? {});
          }
        }
        setPlatformRole((res.data as any)?.platform_role ?? null);
        setImpersonation((res.data as any)?.impersonation ?? null);
      } catch {
        // Only the authoritative boot check logs out on failure. Focus-triggered
        // revalidation swallows transient errors so a network blip can't bounce
        // an actively-working user to /login.
        if (opts?.logoutOnError) logout();
      }
    },
    [
      updateCompanyModules,
      updateCompanyPermissions,
      updateCompanyTheme,
      updateCompanyFeatureFlags,
      setPlatformRole,
      setImpersonation,
      logout,
    ],
  );

  // ── Boot check (runs once on mount) ──
  useEffect(() => {
    if (!isAuthenticated) {
      // Cold load with an empty store but a possibly-valid httpOnly session
      // cookie. Attempt a one-time cookie-based rehydration: if /auth/me
      // returns a user, hydrate the store with the SAME action the login flow
      // uses so the guard renders the app instead of bouncing to /login. A 401
      // (no/expired cookie) just marks bootstrap done → guard redirects.
      authApi.getMe()
        .then((res) => {
          const data = res.data as any;
          if (data?.user) {
            setUser(
              {
                id: data.user.id,
                email: data.user.email,
                first_name: data.user.first_name,
                last_name: data.user.last_name,
                language: data.user.language,
                must_change_password: Boolean(data.user.must_change_password),
                totp_enabled: Boolean(data.user.totp_enabled),
                phone: data.user.phone ?? undefined,
                preferences: data.user.preferences ?? {},
              },
              data.companies ?? [],
              data.default_company_id ?? undefined,
              data.platform_role ?? null,
            );
            setImpersonation(data.impersonation ?? null);
          }
        })
        .catch(() => {
          // No valid cookie — remain logged out.
        })
        .finally(() => {
          // setUser already flips authStatus to 'ready'; this covers the 401
          // path so the guard can stop spinning and redirect to /login.
          setBootstrapped();
        });
      return;
    }
    // Warm store (sessionStorage hydrated within the same tab). It's already
    // trusted — mark bootstrap ready immediately so the guard never shows a
    // needless spinner, then revalidate against the cookie in the background.
    setBootstrapped();
    void revalidateSession({ logoutOnError: true });

    // Load group color palette + favicon + meta tags. Only for tenants with
    // the group module enabled — otherwise the /group/info request 403s and
    // we fall back to the default palette anyway.
    const enabled = useAuthStore.getState().getEnabledModules();
    if (!enabled.includes('group')) {
      setGroupPaletteKey('etg');
      return;
    }
    groupApi.getGroupInfo()
      .then((res) => {
        if (res.data?.color_palette) {
          setGroupPaletteKey(res.data.color_palette);
        }
        // Dynamic favicon from group settings
        const settings = (res.data as any)?.settings || {};
        if (settings.favicon_url) {
          const link = document.querySelector<HTMLLinkElement>('link[rel="icon"][type="image/svg+xml"]')
            || document.querySelector<HTMLLinkElement>('link[rel="icon"]');
          if (link) { link.href = settings.favicon_url; link.type = ''; }
        }
        // Dynamic meta tags from group settings
        if (settings.meta_title) {
          document.title = settings.meta_title;
        }
        if (settings.meta_description) {
          const meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
          if (meta) meta.content = settings.meta_description;
        }
      })
      .catch(() => {
        // Silently fail — use default palette
        setGroupPaletteKey('etg');
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  // ── Live propagation: when the user returns to the app tab/window, re-check
  // /auth/me so Console-side changes (theme, feature flags, modules) apply
  // without a manual reload. Throttled so rapid focus toggles don't spam it.
  useEffect(() => {
    if (!isAuthenticated) return;
    const maybeRevalidate = () => {
      if (document.visibilityState === 'hidden') return;
      if (Date.now() - lastRevalidateRef.current < REVALIDATE_THROTTLE_MS) return;
      void revalidateSession({ logoutOnError: false });
    };
    window.addEventListener('focus', maybeRevalidate);
    document.addEventListener('visibilitychange', maybeRevalidate);
    return () => {
      window.removeEventListener('focus', maybeRevalidate);
      document.removeEventListener('visibilitychange', maybeRevalidate);
    };
  }, [isAuthenticated, revalidateSession]);
}
